<script setup lang="ts">
import { computed, ref } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { PlanEditTarget } from "@/modules/base_price/store";
import {
	CalendarWeek,
	dayAmounts,
	DayMap,
	daysFitMonth,
	rebalanceDays,
} from "@/modules/base_price/utils/dayDistribution";
import { useMonthCalendar } from "@/modules/base_price/composables/useMonthCalendar";
import { useMonthDayRows } from "@/modules/base_price/composables/useMonthDayRows";
import PlanMonthCalendarGrid from "@/modules/base_price/components/PlanMonthCalendarGrid.vue";
import { SEASON_STATE_COLORS, SEASON_STATE_TINTS } from "@/modules/base_price/utils/metricColors";

// Short names for the style block's `v-bind()`.
const AUTO_COLOR = SEASON_STATE_COLORS.auto;
const LOCKED_COLOR = SEASON_STATE_COLORS.locked;
const PINNED_COLOR = SEASON_STATE_COLORS.pinned;
const LOCKED_TINT = SEASON_STATE_TINTS.locked;
const PINNED_TINT = SEASON_STATE_TINTS.pinned;

/**
 * One month of the Seasonality tab, by day: the month's budget — settled in
 * the month graph and fixed here — spread over its days. Each day is AUTO,
 * LOCKED or PINNED exactly as a month is in PlanSeasonalityTab.vue, and auto
 * days follow impressions the way auto months do, so the calendar is that
 * graph one level down rather than a second model. The arithmetic lives in
 * utils/dayDistribution.ts; the frame, the impressions and the per-month
 * rows are shared with the Fill & CPM calendar (useMonthCalendar,
 * useMonthDayRows, PlanMonthCalendarGrid).
 *
 * Like the month graph, this is a true draft: nothing reaches the resolver
 * until Apply.
 */
const props = defineProps<{ target: PlanEditTarget }>();

/** 1-12 — shared with the tab, so the arrows and the breadcrumb agree. */
const month = defineModel<number>("month", { required: true });
/** How values read — shared with the month graph's own toggle. */
const mode = defineModel<"pct" | "abs">("mode", { default: "abs" });

const target = computed(() => props.target);

const {
	dates,
	weeks,
	monthName,
	monthIndex,
	monthKey,
	loadingImpressions,
	shares,
	dayGross,
	weekRange,
	step,
} = useMonthCalendar(target, month, () => seed());

const dayRows = useMonthDayRows(monthKey, dates);

/** What the month graph gave this month — the pot the days divide. */
const budget = computed(() => props.target.monthly_budget[monthIndex.value] ?? 0);
const monthGross = computed(
	() => props.target.monthly_gross_impressions[monthIndex.value] ?? 0
);

// --- Draft ---

const weights = ref<DayMap<number>>({});
const locked = ref<DayMap<boolean>>({});
const pins = ref<DayMap<number | null>>({});
const original = ref<{
	weights: DayMap<number>;
	locked: DayMap<boolean>;
	pins: DayMap<number | null>;
}>({ weights: {}, locked: {}, pins: {} });
const dirty = ref(false);

/**
 * Restores what was applied, not a rendering of it: the locked days from the
 * nearest row that decides this month (its own, or inherited — locked days are
 * rates and travel down the tree like a month shape, month by month), and the
 * pins from this target's own row only, since pins never inherit. Every other
 * day is auto and is filled in from impressions.
 */
const seed = () => {
	const nextWeights: DayMap<number> = { ...dayRows.effectiveDays("day_season") };
	const nextLocked: DayMap<boolean> = Object.fromEntries(
		Object.keys(nextWeights).map((date) => [date, true])
	);
	const nextPins: DayMap<number | null> = { ...dayRows.ownPins() };

	weights.value = rebalanceDays(
		dates.value,
		nextWeights,
		nextLocked,
		nextPins,
		shares.value
	);
	locked.value = nextLocked;
	pins.value = nextPins;
	original.value = {
		weights: { ...weights.value },
		locked: { ...nextLocked },
		pins: { ...nextPins },
	};
	dirty.value = false;
};

const isPinned = (date: string) =>
	pins.value[date] !== undefined && pins.value[date] !== null;

type DayState = "auto" | "locked" | "pinned";

const stateOf = (date: string): DayState =>
	isPinned(date) ? "pinned" : locked.value[date] ? "locked" : "auto";

const amounts = computed(() =>
	dayAmounts(dates.value, weights.value, pins.value, budget.value)
);

const pinnedTotal = computed(() =>
	dates.value.reduce(
		(sum, date) => sum + (isPinned(date) ? (pins.value[date] as number) : 0),
		0
	)
);
const remaining = computed(() => Math.max(0, budget.value - pinnedTotal.value));
const pinnedCount = computed(() => dates.value.filter(isPinned).length);
const lockedCount = computed(
	() => dates.value.filter((date) => stateOf(date) === "locked").length
);

const commit = (
	nextWeights: DayMap<number>,
	nextLocked: DayMap<boolean>,
	nextPins: DayMap<number | null>
) => {
	weights.value = rebalanceDays(
		dates.value,
		nextWeights,
		nextLocked,
		nextPins,
		shares.value
	);
	locked.value = nextLocked;
	pins.value = nextPins;
	dirty.value = true;
};

/** Moves days into a state, as the month graph's cycle button does for a month. */
const setState = (list: string[], state: DayState) => {
	const nextLocked = { ...locked.value };
	const nextPins = { ...pins.value };

	list.forEach((date) => {
		if (state === "auto") {
			delete nextPins[date];
			delete nextLocked[date];
		}

		if (state === "locked") {
			delete nextPins[date];
			nextLocked[date] = true;
		}

		// Pins at whatever the day is worth right now, so the step is
		// visually a no-op — what changes is that the amount stops moving.
		if (state === "pinned" && !isPinned(date)) {
			nextPins[date] = Math.round(amounts.value[date] ?? 0);
			delete nextLocked[date];
		}
	});

	commit({ ...weights.value }, nextLocked, nextPins);
};

const NEXT_STATE: Record<DayState, DayState> = {
	auto: "locked",
	locked: "pinned",
	pinned: "auto",
};

const cycleDay = (date: string) => setState([date], NEXT_STATE[stateOf(date)]);

const weekState = (week: CalendarWeek): DayState | "mixed" => {
	const states = week.inMonth.map(stateOf);

	return states.every((state) => state === states[0]) ? states[0] : "mixed";
};

/** A mixed week locks first — the step every one of its days can take. */
const cycleWeek = (week: CalendarWeek) => {
	const state = weekState(week);

	setState(week.inMonth, state === "mixed" ? "locked" : NEXT_STATE[state]);
};

/**
 * Gives the unpinned days in `list` a combined weight, split between them by
 * impressions so their own pattern survives, and locks them — editing an
 * auto day locks it, exactly as dragging a month does.
 */
const setWeights = (list: string[], total: number) => {
	const free = list.filter((date) => !isPinned(date));
	if (!free.length) return;

	const shareSum = free.reduce((sum, date) => sum + (shares.value[date] ?? 0), 0);
	const nextWeights = { ...weights.value };
	const nextLocked = { ...locked.value };

	free.forEach((date) => {
		nextWeights[date] =
			shareSum > 0
				? (Math.max(0, total) * (shares.value[date] ?? 0)) / shareSum
				: Math.max(0, total) / free.length;
		nextLocked[date] = true;
	});

	commit(nextWeights, nextLocked, { ...pins.value });
};

/** Euros for unpinned days, as the weight that earns them out of what the pins leave. */
const weightForEuros = (euros: number): number =>
	remaining.value > 0 ? (euros / remaining.value) * 100 : 0;

const onDayInput = (date: string, raw: string) => {
	if (isPinned(date)) {
		commit(
			{ ...weights.value },
			{ ...locked.value },
			{ ...pins.value, [date]: Math.max(0, parseEuros(raw)) }
		);

		return;
	}

	setWeights(
		[date],
		mode.value === "abs" ? weightForEuros(parseEuros(raw)) : Number(raw)
	);
};

/**
 * A week of nothing but pins has no weight to give, so its euros are spread
 * over the pins themselves — in proportion to what each already holds, so the
 * week keeps its pattern, and evenly when they hold nothing yet. The days stay
 * pinned: a pin is an exact amount, and the week edit is just a faster way of
 * setting several at once.
 */
const setWeekPins = (week: CalendarWeek, euros: number) => {
	const total = Math.max(0, euros);
	const current = week.inMonth.reduce(
		(sum, date) => sum + (pins.value[date] as number),
		0
	);
	const nextPins = { ...pins.value };

	week.inMonth.forEach((date) => {
		nextPins[date] = Math.round(
			current > 0
				? (total * (pins.value[date] as number)) / current
				: total / week.inMonth.length
		);
	});

	commit({ ...weights.value }, { ...locked.value }, nextPins);
};

const onWeekInput = (week: CalendarWeek, raw: string) => {
	// A fully pinned week reads in euros whichever way the toggle points.
	if (weekPinnedOnly(week)) {
		setWeekPins(week, parseEuros(raw));

		return;
	}

	// In % the week reads as its share of the month, so a typed share is
	// that share of the month's budget in euros.
	const euros =
		mode.value === "pct"
			? (Number(raw) / 100) * budget.value
			: parseEuros(raw);

	const pinnedInWeek = week.inMonth.reduce(
		(sum, date) => sum + (isPinned(date) ? (pins.value[date] as number) : 0),
		0
	);
	setWeights(week.inMonth, weightForEuros(euros - pinnedInWeek));
};

const followImpressionShare = () => {
	commit({ ...weights.value }, {}, { ...pins.value });
};

const clearAllPins = () => {
	commit({ ...weights.value }, { ...locked.value }, {});
};

// --- Display ---

const parseEuros = (raw: string): number => Number(raw.replace(/[^0-9]/g, "") || 0);

/** Which box is being typed in: euros read grouped, but edit as plain digits. */
const focusedInput = ref<string | null>(null);

const isCurrency = (date: string) => isPinned(date) || mode.value === "abs";

const dayText = (date: string): string => {
	if (!isCurrency(date)) return (weights.value[date] ?? 0).toFixed(1);

	const value = Math.round(amounts.value[date] ?? 0);

	return focusedInput.value === date
		? String(value)
		: formatNumber(value, "number_no_fraction");
};

const weekPinnedOnly = (week: CalendarWeek) => week.inMonth.every(isPinned);
const weekIsCurrency = (week: CalendarWeek) =>
	mode.value === "abs" || weekPinnedOnly(week);

/**
 * In %, a week reads as its share of the month's budget — pins included — so
 * it moves whenever any day in it does, pinned or not, and reads on the same
 * scale as the impression share beneath it. A week of nothing but pins reads
 * in euros, as a pinned day does.
 */
const weekText = (week: CalendarWeek): string => {
	if (!weekIsCurrency(week)) {
		const amount = week.inMonth.reduce(
			(sum, date) => sum + (amounts.value[date] ?? 0),
			0
		);

		return (budget.value > 0 ? (amount / budget.value) * 100 : 0).toFixed(1);
	}

	const value = Math.round(
		week.inMonth.reduce((sum, date) => sum + (amounts.value[date] ?? 0), 0)
	);

	return focusedInput.value === `week-${week.isoWeek}`
		? String(value)
		: formatNumber(value, "number_no_fraction");
};

const weekImpressionShare = (week: CalendarWeek): number =>
	week.inMonth.reduce((sum, date) => sum + (shares.value[date] ?? 0), 0);

const monthShare = (date: string): number =>
	budget.value > 0 ? ((amounts.value[date] ?? 0) / budget.value) * 100 : 0;

/** Bars are drawn on one scale per month: the largest day, or the largest impression share. */
const maxShare = computed(
	() =>
		Math.max(
			...dates.value.map((date) => Math.max(monthShare(date), shares.value[date] ?? 0)),
			1
		) * 1.15
);

const barWidth = (date: string) =>
	`${Math.min(100, (monthShare(date) / maxShare.value) * 100)}%`;
const refLeft = (date: string) =>
	`${Math.min(100, ((shares.value[date] ?? 0) / maxShare.value) * 100)}%`;

const BAR_COLOR: Record<DayState, string> = SEASON_STATE_COLORS;

const STATE_TITLE: Record<DayState, string> = {
	auto: "Follows impressions — click to hold its share",
	locked: "Holding its share — click to pin an exact amount",
	pinned: "Holding an exact amount — click for auto",
};

/** Why a day gets what it gets, in the terms the bar shows it in. */
const whyText = (date: string): string => {
	const share = monthShare(date);
	const diff = share - (shares.value[date] ?? 0);
	const versus =
		Math.abs(diff) < 0.05
			? "exactly its impression share"
			: `${Math.abs(diff).toFixed(1)} pt ${diff > 0 ? "above" : "below"} its impression share`;
	const head = `${formatNumberForScale("money", amounts.value[date] ?? 0)} · ${share.toFixed(1)}% of ${monthName.value}`;

	switch (stateOf(date)) {
		case "pinned":
			return `${head}. Pinned to an exact amount, ${versus}.`;
		case "locked":
			return `${head}. Locked at its share, ${versus}; auto days absorb the difference.`;
		default:
			return `${head}. Auto: gets budget in proportion to its ${formatNumberForScale("number_no_fraction", dayGross(date))} impressions, scaled to what locked and pinned days leave.`;
	}
};

// --- Summary ---

const valid = computed(() => daysFitMonth(dates.value, pins.value, budget.value));
const overshoot = computed(() => Math.max(0, pinnedTotal.value - budget.value));

const freeSplit = computed(() => {
	let lockedAmount = 0;
	let autoAmount = 0;

	dates.value.forEach((date) => {
		if (stateOf(date) === "locked") lockedAmount += amounts.value[date] ?? 0;
		if (stateOf(date) === "auto") autoAmount += amounts.value[date] ?? 0;
	});

	return { locked: lockedAmount, auto: autoAmount };
});

const allocated = computed(
	() =>
		pinnedTotal.value +
		(pinnedCount.value < dates.value.length ? remaining.value : 0)
);

const footColor = computed(() =>
	valid.value ? "success" : overshoot.value > 0 ? "error" : "warning"
);

const footMain = computed(
	() =>
		`Allocated ${formatNumberForScale("money", allocated.value)} of ${formatNumberForScale(
			"money",
			budget.value
		)}`
);

const footSub = computed(() => {
	if (overshoot.value > 1) {
		return `Pinned days ask ${formatNumberForScale("money", overshoot.value)} more than ${monthName.value} has — lower a pinned day.`;
	}
	if (!valid.value) {
		return `Every day is pinned, so they have to total exactly ${formatNumberForScale("money", budget.value)}.`;
	}
	if (pinnedCount.value || lockedCount.value) {
		return `${pinnedCount.value} pinned · ${lockedCount.value} locked · ${
			dates.value.length - pinnedCount.value - lockedCount.value
		} auto days share what's left by impressions.`;
	}
	return `Every day follows its impressions. ${monthName.value}'s total is set in the month view.`;
});

// --- Apply / Cancel ---

/**
 * Writes this month into the target's own day rows, leaving every other month
 * in them alone — locked days inherit month by month, so applying December
 * makes December this target's own and nothing else.
 *
 * A month with no locked days is still listed when an ancestor locks some in
 * it: without that, "every day follows impressions" here would quietly hand
 * it the ancestor's locks instead. With nothing above to shadow it is left
 * out, so the row stays as small as what it actually says. Pins never
 * inherit, so an empty pin row is simply removed.
 */
const apply = () => {
	if (!valid.value) return;

	const round2 = (value: number) => Math.round(value * 100) / 100;

	const lockedShares = Object.fromEntries(
		dates.value
			.filter((date) => stateOf(date) === "locked")
			.map((date) => [date, round2(weights.value[date] ?? 0)])
	);
	const pinnedAmounts = Object.fromEntries(
		dates.value
			.filter(isPinned)
			.map((date) => [date, Math.round(pins.value[date] as number)])
	);

	dayRows.writeMonth("day_season", lockedShares);
	dayRows.writePins(pinnedAmounts);

	original.value = {
		weights: { ...weights.value },
		locked: { ...locked.value },
		pins: { ...pins.value },
	};
	dirty.value = false;
};

/**
 * Does this target decide anything of its own in this month — locked days, or
 * pins? Whether PlanSeasonalityTab shows the Reset to inherited button.
 */
const ownsMonth = computed(
	() =>
		dayRows.decidesOwn("day_season") ||
		Object.keys(dayRows.ownPins()).length > 0
);

/**
 * Hands this month back to whatever is above: takes it out of the target's
 * own locked-days row and drops its own pins in it, leaving every other month
 * as it was. The days then follow the nearest ancestor that decides this
 * month, or impressions when none does.
 */
const resetToInherited = () => {
	dayRows.clearMonth("day_season");
	dayRows.writePins({});

	seed();
};

const cancel = () => {
	weights.value = { ...original.value.weights };
	locked.value = { ...original.value.locked };
	pins.value = { ...original.value.pins };
	dirty.value = false;
};

// The tab renders the reset in its own toolbar, next to the %/€ toggle.
defineExpose({ ownsMonth, resetToInherited });

const DAY_TONE: Record<DayState, string> = {
	auto: "",
	locked: "calendar-cell--tone-locked",
	pinned: "calendar-cell--tone-pinned",
};
</script>

<template>
	<div>
		<div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-4">
			<div>
				<div class="text-body-1 font-weight-bold">
					Distribute {{ monthName }}'s
					{{ formatNumberForScale("money", budget) }} across its days
				</div>
				<div class="text-caption text-medium-emphasis d-flex align-center ga-1">
					<v-icon icon="fa-light fa-lock" size="10" />
					The month's total is set in the month view ·
					{{ formatNumberForScale("number_no_fraction", monthGross) }}
					impressions this month
				</div>
			</div>

			<div class="d-flex align-center ga-1">
				<v-btn
					icon="fa-light fa-chevron-left"
					size="small"
					variant="text"
					:disabled="month === 1"
					aria-label="Previous month"
					@click="step(-1)"
				/>
				<v-btn
					icon="fa-light fa-chevron-right"
					size="small"
					variant="text"
					:disabled="month === 12"
					aria-label="Next month"
					@click="step(1)"
				/>
			</div>
		</div>

		<!-- Same readout as the month graph: what the days add up to, against what the month has to give them. -->
		<div class="season-summary-card mb-4">
			<div class="text-body-2 font-weight-bold" :class="`text-${footColor}`">
				{{ footMain }}
			</div>
			<div class="text-caption text-medium-emphasis">{{ footSub }}</div>
			<div class="season-summary-bar">
				<div
					:style="{ flex: Math.min(pinnedTotal, budget) || 0.0001 }"
					class="season-summary-pinned"
					title="Pinned days — exact amounts"
				/>
				<div
					:style="{ flex: freeSplit.locked || 0.0001 }"
					class="season-summary-locked"
					title="Locked days — share set by hand"
				/>
				<div
					:style="{ flex: freeSplit.auto || 0.0001 }"
					class="season-summary-auto"
					title="Auto days — follow impressions"
				/>
				<div
					:style="{ flex: overshoot || 0.0001 }"
					class="season-summary-over"
					title="Pinned beyond the month's budget"
				/>
			</div>
		</div>

		<div class="d-flex ga-4 flex-wrap mb-2 text-caption text-medium-emphasis">
			<span class="d-flex align-center ga-1">
				<span class="legend-square" :style="{ background: BAR_COLOR.auto }" />
				auto — follows impressions
			</span>
			<span class="d-flex align-center ga-1">
				<span class="legend-square" :style="{ background: BAR_COLOR.locked }" />
				locked — share set by hand
			</span>
			<span class="d-flex align-center ga-1">
				<span class="legend-square" :style="{ background: BAR_COLOR.pinned }" />
				pinned — exact amount
			</span>
			<span class="d-flex align-center ga-1">
				<span class="legend-tick" />
				impression share
			</span>
		</div>
		<div class="text-caption text-medium-emphasis mb-3">
			An auto day gets budget in proportion to its impressions, so its bar meets
			its impression mark. Lock or pin a day to move it off; the auto days absorb
			the difference.
		</div>

		<v-progress-linear
			v-if="loadingImpressions"
			indeterminate
			color="primary"
			class="mb-2"
		/>

		<plan-month-calendar-grid
			:weeks="weeks"
			:loading="loadingImpressions"
			ghost-hint="distribute it there"
			:day-class="(date: string) => DAY_TONE[stateOf(date)]"
		>
			<template #day="{ day }">
				<div class="d-flex align-center justify-space-between">
					<span class="text-body-2 font-weight-bold">{{ day.day }}</span>
					<button
						class="season-state-btn calendar-state-btn"
						:class="{
							'season-state-btn--locked': stateOf(day.date) === 'locked',
							'season-state-btn--pinned': stateOf(day.date) === 'pinned',
						}"
						:title="STATE_TITLE[stateOf(day.date)]"
						@click="cycleDay(day.date)"
					>
						{{ stateOf(day.date).toUpperCase() }}
					</button>
				</div>

				<div
					class="season-input-box"
					:class="{
						'season-input-box--locked': stateOf(day.date) === 'locked',
						'season-input-box--pinned': stateOf(day.date) === 'pinned',
					}"
				>
					<span v-if="isCurrency(day.date)" class="season-input-affix">€</span>
					<input
						:type="isCurrency(day.date) ? 'text' : 'number'"
						:inputmode="isCurrency(day.date) ? 'numeric' : undefined"
						:step="isCurrency(day.date) ? undefined : 0.1"
						:value="dayText(day.date)"
						:aria-label="`${day.date} budget`"
						@focus="focusedInput = day.date"
						@blur="focusedInput = null"
						@change="onDayInput(day.date, ($event.target as HTMLInputElement).value)"
					/>
					<span v-if="!isCurrency(day.date)" class="season-input-affix">%</span>
				</div>

				<v-tooltip :text="whyText(day.date)" max-width="280">
					<template v-slot:activator="{ props: tooltipProps }">
						<div v-bind="tooltipProps" class="calendar-bar">
							<div class="calendar-bar-track" />
							<div
								class="calendar-bar-fill"
								:style="{ width: barWidth(day.date), background: BAR_COLOR[stateOf(day.date)] }"
							/>
							<div class="calendar-bar-ref" :style="{ left: refLeft(day.date) }" />
						</div>
					</template>
				</v-tooltip>

				<div class="text-caption text-medium-emphasis calendar-caption">
					Impr. {{ (shares[day.date] ?? 0).toFixed(1) }}%
				</div>
			</template>

			<template #week="{ week }">
				<div class="d-flex align-center justify-space-between">
					<span class="text-body-2 font-weight-bold">Wk {{ week.isoWeek }}</span>
					<button
						class="season-state-btn calendar-state-btn"
						:class="{
							'season-state-btn--locked': weekState(week) === 'locked',
							'season-state-btn--pinned': weekState(week) === 'pinned',
						}"
						title="Applies to every day of this week in this month"
						@click="cycleWeek(week)"
					>
						{{ weekState(week).toUpperCase() }}
					</button>
				</div>

				<div class="season-input-box">
					<span v-if="weekIsCurrency(week)" class="season-input-affix">€</span>
					<input
						:type="weekIsCurrency(week) ? 'text' : 'number'"
						:inputmode="weekIsCurrency(week) ? 'numeric' : undefined"
						:step="weekIsCurrency(week) ? undefined : 0.1"
						:value="weekText(week)"
						:aria-label="`Week ${week.isoWeek} budget`"
						@focus="focusedInput = `week-${week.isoWeek}`"
						@blur="focusedInput = null"
						@change="onWeekInput(week, ($event.target as HTMLInputElement).value)"
					/>
					<span v-if="!weekIsCurrency(week)" class="season-input-affix">%</span>
				</div>

				<div class="text-caption text-medium-emphasis calendar-caption">
					{{ weekRange(week) }}
					<template v-if="week.inMonth.length < 7">
						· {{ week.inMonth.length }} of 7 days
					</template>
				</div>
				<div class="text-caption text-medium-emphasis calendar-caption">
					Impr. {{ weekImpressionShare(week).toFixed(1) }}%
				</div>
			</template>
		</plan-month-calendar-grid>

		<div class="text-caption text-medium-emphasis mt-2">
			Greyed days belong to the neighbouring month and are distributed there.
			A week's value spreads over its own days by impressions and locks them;
			in a fully pinned week it rescales the pins instead.
		</div>

		<div class="d-flex ga-2 flex-wrap my-4">
			<v-btn size="small" variant="tonal" @click="followImpressionShare">
				Follow impression share
			</v-btn>
			<v-btn
				v-if="pinnedCount > 0"
				size="small"
				variant="outlined"
				@click="clearAllPins"
			>
				Clear {{ pinnedCount }} pin{{ pinnedCount === 1 ? "" : "s" }}
			</v-btn>
		</div>

		<div class="d-flex justify-end ga-2">
			<v-btn variant="text" color="error" :disabled="!dirty" @click="cancel">
				Cancel
			</v-btn>
			<v-btn
				variant="tonal"
				color="primary"
				:disabled="!valid || !dirty"
				@click="apply"
			>
				Apply {{ monthName }}
			</v-btn>
		</div>
	</div>
</template>

<style scoped lang="scss">
// The month graph's own pieces (PlanSeasonalityTab.vue), repeated because
// both components scope their styles — the calendar has to read as the same
// editor one level down.
.legend-square {
	width: 9px;
	height: 9px;
	border-radius: 2px;
	display: inline-block;
}

.legend-tick {
	width: 0;
	height: 10px;
	border-left: 1.5px dashed rgba(var(--v-theme-on-surface), 0.6);
	display: inline-block;
}

.season-summary-card {
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-radius: 10px;
	padding: 14px 20px;
}

.season-summary-bar {
	display: flex;
	height: 8px;
	border-radius: 4px;
	overflow: hidden;
	background: rgba(var(--v-theme-on-surface), 0.08);
	margin-top: 8px;
}

.season-summary-pinned {
	background: v-bind(PINNED_COLOR);
}

.season-summary-locked {
	background: v-bind(LOCKED_COLOR);
}

.season-summary-auto {
	background: v-bind(AUTO_COLOR);
}

.season-summary-over {
	background: v-bind(PINNED_COLOR);
	border-left: 2px solid rgb(var(--v-theme-surface));
}

.season-input-box {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 6px;
	height: 28px;
	border: 1.5px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	background: rgb(var(--v-theme-surface));
}

.season-input-box--locked {
	border-color: v-bind(LOCKED_COLOR);
}

.season-input-box--pinned {
	border-color: v-bind(PINNED_COLOR);
}

.season-input-box input {
	width: 100%;
	min-width: 0;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	text-align: center;
	font-variant-numeric: tabular-nums;
}

.season-input-box input::-webkit-outer-spin-button,
.season-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.season-input-affix {
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	padding: 0 4px;
}

.season-state-btn {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
	background: transparent;
	color: rgba(var(--v-theme-on-surface), 0.6);
	border-radius: 6px;
	padding: 3px 0;
	font-size: 9.5px;
	letter-spacing: 0.08em;
	cursor: pointer;
}

// Text stays in the normal ink: the state colours are too light to read.
.season-state-btn--locked {
	border-color: v-bind(LOCKED_COLOR);
	background: v-bind(LOCKED_TINT);
	color: rgb(var(--v-theme-on-surface));
}

.season-state-btn--pinned {
	border-color: v-bind(PINNED_COLOR);
	background: v-bind(PINNED_TINT);
	color: rgb(var(--v-theme-on-surface));
}

// --- Calendar ---

.calendar-state-btn {
	padding: 2px 6px;
	font-size: 8.5px;
}

.calendar-bar {
	position: relative;
	height: 14px;
	margin-top: 8px;
}

.calendar-bar-track {
	position: absolute;
	left: 0;
	right: 0;
	top: 4px;
	height: 6px;
	border-radius: 3px;
	background: rgba(var(--v-theme-on-surface), 0.08);
}

.calendar-bar-fill {
	position: absolute;
	left: 0;
	top: 4px;
	height: 6px;
	border-radius: 3px;
	transition: width 0.1s ease;
}

// The month graph's dashed impression line, stood upright.
.calendar-bar-ref {
	position: absolute;
	top: 0;
	height: 14px;
	margin-left: -1px;
	border-left: 1.5px dashed rgba(var(--v-theme-on-surface), 0.7);
}

.calendar-caption {
	line-height: 1.3;
	margin-top: 2px;
}
</style>
